import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    Row,
    Tag,
} from '@zillow/zrs-comet';

import {
    distanceInWordsStrict,
    format,
} from 'date-fns';

// selectors
import { RoutingSelectors } from 'app/shared/selectors';

// models
import ListingViewStatisticsModel from 'app/shared/model/ListingViewStatistics';

import ConnectedModuleCardConstants from 'app/shared/constants/ConnectedModuleCardConstants';

// components
import ConnectedModuleCard from 'app/shared/components/ConnectedModuleCard';
import ConnectedNetworkView from 'app/shared/components/ConnectedNetworkView';

// utils
import ZRMUtils from '@zg-rentals/zrm-utils-js/lib/utils';
import { STATUS_KEY, getTagTypeByKey } from 'app/shared/utils/statusTagUtils';

// actions
import MarketingActions from 'app/shared/actions/MarketingActions';
import PostingActions from 'app/shared/actions/PostingActions';

// constants
import { LISTING_SUMMARY_DISPLAY_STATUS } from 'app/shared/constants/ListingSummaryConstants';

// sub components
import Alert from './components/Alert';
import CTA from './components/CTA';
import Comps from './components/Comps';
import Details from './components/Details';
import Statistics from './components/Statistics';
import Status from './components/Status';

// style
import {
    StyledBody,
    StyledCTAComponent,
    StyledCol,
    StyledFlex,
    StyledHR,
    StyledStatusContainer,
} from './StyledComponents';

const propTypes = {
    pListingSummary: PropTypes.object,
    pListingStatistics: PropTypes.object,
    minHeightXL: PropTypes.string,
    updateMinModuleHeight: PropTypes.func,
};

const defaultProps = {
    pListingSummary: null,
    pListingStatistics: ListingViewStatisticsModel({}),
    minHeightXL: ConnectedModuleCardConstants.FIND_TENANTS_MODULE_MIN_HEIGHT_XL,
    updateMinModuleHeight: () => {},
};

const tempListingSummaryStatusList = [
    LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE,
    LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHING,
    LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHED,
    LISTING_SUMMARY_DISPLAY_STATUS.PARTIALLY_PUBLISHED,
    LISTING_SUMMARY_DISPLAY_STATUS.DECLINED,
];

const DEFAULT_ERROR_MESSAGE = 'No information found for this listing.';

const ConnectedListing = ({
    pListingStatistics,
    pListingSummary,
    minHeightXL,
    updateMinModuleHeight,
}) => {
    const dispatch = useDispatch();

    // TODO: remove search when testing is complete
    // selectors
    const search = useSelector(state => RoutingSelectors.makeSearch(state));
    const listingAlias = useSelector(state => RoutingSelectors.makeListingId(state));

    // state
    const [queryStatus] = useState(ZRMUtils.queryUtils.parse(search).status);
    const [listingSummaryError, setListingSummaryError] = useState(false);
    const [listingSummary, setListingSummary] = useState(pListingSummary);
    const [listingStatistics, setListingStatistics] = useState(pListingStatistics);
    const [listingComps, setListingComps] = useState();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // listingAlias is required
        if (!listingAlias) {
            return;
        }

        // summary
        const getListingSummaryPromise = dispatch(PostingActions.property.getListingSummary(listingAlias))
            .then((response) => {
                setListingSummary(response);
            })
            .catch((errorRMWModel) => {
                setListingSummaryError(errorRMWModel);
            });

        // statistics
        const fetchCompStatsPromise = dispatch(MarketingActions.fetchCompStatsByListingAlias(listingAlias))
            .then((response) => {
                setListingStatistics(response);
            }).catch((errorRMW) => {
                setListingStatistics(errorRMW);
            });

        // comps
        const fetchCompsPromise = dispatch(MarketingActions.fetchCompsByListingAlias(listingAlias))
            .then((response) => {
                setListingComps(response);
            }).catch((errorRMW) => {
                setListingComps(errorRMW);
            });

        Promise.all([getListingSummaryPromise, fetchCompStatsPromise, fetchCompsPromise]).then(() => {
            setIsLoading(false);
            updateMinModuleHeight();
        });
    }, [listingAlias]);

    const {
        dateActivatedTimestamp,
        dateExpirationTimestamp,
        paidInclusion,
        rentZestimate,
        canDeactivate,
    } = listingSummary || {};
    const { isAutoBillEnabled, renewalPrice } = paidInclusion || {};

    // TODO: remove function after testing is complete
    // get status via function to handle query param overrides
    // ex: ?status=declined
    const getStatus = () => {
        // check for random status
        const indexVal = queryStatus === 'random'
            ? Math.floor(Math.random() * 5)
            : tempListingSummaryStatusList.indexOf(queryStatus);

        // check if a status index is found
        const response = indexVal !== -1
            ? tempListingSummaryStatusList[indexVal]
            : listingSummary.syndication.overview.displayStatus;

        return response.toLowerCase();
    };

    const status = listingSummary && getStatus();

    const lastListed = dateActivatedTimestamp && format(new Date(Number(dateActivatedTimestamp)), 'MMM YYYY');
    const listedFor = dateActivatedTimestamp && distanceInWordsStrict(
        new Date(Number(dateActivatedTimestamp)),
        new Date()
    );
    const renews = dateExpirationTimestamp && format(new Date(Number(dateExpirationTimestamp)), 'MMM D');

    const renderTag = () => (
        status && (
            <Tag
                appearance={getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, status)}
                size="sm"
            >
                {status}
            </Tag>
        )
    );

    const renderCTAComponent = () => (
        <StyledCTAComponent>
            {renderTag()}
        </StyledCTAComponent>
    );

    const renderVON = () => {
        const lowerCaseStatus = status.toLowerCase();

        // do not display
        if (lowerCaseStatus === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE
            || lowerCaseStatus === LISTING_SUMMARY_DISPLAY_STATUS.DECLINED) {
            return null;
        }

        return (
            <ConnectedNetworkView
                label="View on network"
                size="sm"
                status={status}
                syndication={listingSummary.syndication}
            />
        );
    };

    const renderComps = () => {
        let message = listingComps.hasOwnProperty('message')
            ? listingComps.message
            : DEFAULT_ERROR_MESSAGE;

        const comparableFloorplans = listingComps.hasOwnProperty('similarFloorplans') &&
            listingComps.similarFloorplans.floorplans;

        if (listingComps.hasOwnProperty('message')) {
            return <StyledFlex>{message}</StyledFlex>;
        }

        if (comparableFloorplans.length === 0) {
            message = 'We are unable to find any comparable listings nearby.';

            return <StyledFlex>{message}</StyledFlex>;
        }

        return (
            <Comps listingCompsModel={listingComps} status={status} />
        );
    };

    const renderStatistics = () => {
        const message = listingStatistics.hasOwnProperty('message')
            ? listingStatistics.message
            : DEFAULT_ERROR_MESSAGE;

        if (listingStatistics.hasOwnProperty('message')) {
            return <StyledFlex overflow="auto">{message}</StyledFlex>;
        }

        // startDate will be formatted to a String
        if (listingStatistics.stats.startDate !== '') {
            return (
                <Statistics listingStatistics={listingStatistics} />
            );
        }

        return null;
    };

    // render comps or alert depending on the status
    const renderByStatus = () => {
        switch (status) {
            case LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE:
                return renderComps();

            case LISTING_SUMMARY_DISPLAY_STATUS.PARTIALLY_PUBLISHED:
            case LISTING_SUMMARY_DISPLAY_STATUS.DECLINED:
                return (
                    <Alert
                        status={status}
                        listingSummary={listingSummary}
                    />
                );

            default:
                return renderStatistics();
        }
    };

    return (
        <div className="ConnectedListing">
            {listingSummaryError ?
                (
                    <ConnectedModuleCard
                        title="Listing"
                        isLoading={false}
                        size="sm"
                    >
                        <StyledFlex>{listingSummaryError.message}</StyledFlex>
                    </ConnectedModuleCard>
                ) : (
                    <ConnectedModuleCard
                        title="Listing"
                        ctaComponent={renderCTAComponent()}
                        titleGrow={0}
                        isLoading={isLoading}
                        size="sm"
                        minHeightXL={minHeightXL}
                    >
                        {isLoading === false && (
                            <StyledFlex>
                                <StyledStatusContainer>
                                    <Status
                                        tagComponent={renderTag()}
                                        vonComponent={renderVON()}
                                    />
                                </StyledStatusContainer>

                                <Row>
                                    <StyledCol
                                        xs={12}
                                        lg={9}
                                        xl={12}
                                    >
                                        <StyledBody>
                                            <Details
                                                isAutoBillEnabled={isAutoBillEnabled}
                                                lastListed={lastListed}
                                                listedFor={listedFor}
                                                renews={renews}
                                                rentZestimate={rentZestimate}
                                                status={status}
                                                renewalPrice={renewalPrice}
                                            />
                                            <StyledHR />
                                            {renderByStatus()}
                                        </StyledBody>
                                    </StyledCol>

                                    <StyledCol
                                        xs={12}
                                        lg={3}
                                        xl={12}
                                    >
                                        <CTA
                                            canDeactivate={canDeactivate}
                                            listingAlias={listingAlias}
                                            status={status}
                                            vonComponent={renderVON()}
                                        />
                                    </StyledCol>
                                </Row>
                            </StyledFlex>
                        )}
                    </ConnectedModuleCard>
                )
            }
        </div>
    );
};

/**
 * props with the prefix of 'p'
 *
 * hacks to pass values to useState for unit testing
 * also used to set a default data model to avoid
 * large amount of data integrity checks for nested objects
 */
ConnectedListing.propTypes = propTypes;
ConnectedListing.defaultProps = defaultProps;

export default ConnectedListing;
